import { lazy, Suspense, useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Home from "@/pages/Home";

// Lazy import resiliente: su reti mobili instabili (LTE) o dopo un redeploy con nuovi hash dei chunk,
// un import dinamico può fallire e lasciare lo schermo nero. Qui riproviamo una volta e, se il chunk
// non si carica, ricarichiamo la pagina UNA sola volta (niente loop) per recuperare i chunk aggiornati.
const lazyRetry = (factory) => lazy(() => factory().then((mod) => {
  sessionStorage.removeItem("if_chunk_reload");
  return mod;
}).catch((error) => {
  const key = "if_chunk_reload";
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
    return new Promise(() => {});
  }
  throw error;
}));

// Error boundary: se un chunk non si carica anche dopo il reload, mostra un messaggio + "Ricarica"
// invece di una pagina completamente nera.
class RouteErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) {
      return (
        <div className="min-h-screen bg-[#0b0b0b] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="route-error">
          <p className="text-white/80 text-sm">Contenuto non caricato. Controlla la connessione e riprova.</p>
          <button onClick={() => { sessionStorage.removeItem("if_chunk_reload"); window.location.reload(); }}
            className="h-10 px-5 rounded-full bg-[#D4AF37] text-black text-sm font-bold" data-testid="route-error-reload">Ricarica</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Code-splitting: solo la Home è nel bundle iniziale, le altre pagine si caricano quando servono
const Configurator = lazyRetry(() => import("@/pages/Configurator"));
const SectorPage = lazyRetry(() => import("@/pages/SectorPage"));
const BlogPost = lazyRetry(() => import("@/pages/Blog"));
const BlogIndex = lazyRetry(() => import("@/pages/Blog").then((m) => ({ default: m.BlogIndex })));
const Checkout = lazyRetry(() => import("@/pages/Checkout"));
const PaymentSuccess = lazyRetry(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazyRetry(() => import("@/pages/PaymentCancel"));
const Legal = lazyRetry(() => import("@/pages/Legal"));
const Pricing = lazyRetry(() => import("@/pages/Pricing"));
const ReviewPage = lazyRetry(() => import("@/pages/ReviewPage"));
const OwnerArea = lazyRetry(() => import("@/pages/OwnerArea"));
const Admin = lazyRetry(() => import("@/pages/Admin"));
const accountPage = (name) => lazyRetry(() => import("@/pages/Account").then((m) => ({ default: m[name] })));
const LoginPage = accountPage("LoginPage");
const RegisterPage = accountPage("RegisterPage");
const ForgotPage = accountPage("ForgotPage");
const ResetPage = accountPage("ResetPage");
const AccountPage = lazyRetry(() => import("@/pages/Dashboard"));
const Studio = lazyRetry(() => import("@/pages/Studio"));
import { ChatWidget } from "@/components/ChatWidget";
import { AuthProvider } from "@/context/AuthContext";
import { EmailVerificationBanner, VerifyEmailPage } from "@/components/EmailVerification";
import { CountryLanguage } from "@/components/CountryLanguage";
import "@/styles/enterprise-refresh.css";
import { GALoader } from "@/components/GALoader";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AuthCallback } from "@/components/SocialLogin";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Scala compatta mobile ovunque tranne nel checkout (form)
    document.documentElement.classList.toggle("if-compact", !pathname.startsWith("/checkout"));
  }, [pathname]);
  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <RouteErrorBoundary>
    <Suspense fallback={<div className="min-h-screen bg-[#0b0b0b] flex items-center justify-center" data-testid="route-loading"><span className="inline-block w-8 h-8 rounded-full border-2 border-white/15 border-t-[#D4AF37] animate-spin" aria-label="Caricamento" /></div>}>
          <CountryLanguage />
          <EmailVerificationBanner />
          <Routes>
            <Route path="/verifica-email" element={<VerifyEmailPage />} />
      <Route path="/" element={<Home />} />
      <Route path="/configura" element={<Configurator />} />
      <Route path="/settori/:slug" element={<SectorPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/checkout/:orderId" element={<Checkout />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/legale/:doc" element={<Legal />} />
      <Route path="/r/:orderId" element={<ReviewPage />} />
      <Route path="/gestisci/:orderId" element={<OwnerArea />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/accedi" element={<LoginPage />} />
      <Route path="/registrati" element={<RegisterPage />} />
      <Route path="/prezzi" element={<Pricing />} />
      <Route path="/password-dimenticata" element={<ForgotPage />} />
      <Route path="/reset-password" element={<ResetPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/studio" element={<Studio />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
        <GALoader />
        <WhatsAppButton />
        <ChatWidget />
        <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
